/* ============================================================================
   LANDING — Mini-terrain animé (« Un sport, un terrain reconnaissable »)
   ----------------------------------------------------------------------------
   Un petit terrain SVG se dessine ligne par ligne (stroke-dashoffset), marque
   une pause, s'efface, puis enchaîne sur le sport suivant — en boucle.

   Les tracés reproduisent FIDÈLEMENT ceux de l'application (src/render.js) :
   mêmes fractions de largeur/hauteur pour les surfaces, cercles, raquettes,
   arcs et cages, mêmes couleurs de fond (bgForSport) et lignes blanches.

   • Décoratif : conteneur aria-hidden ; si ce module ne tourne pas, rien ne
     casse (le div reste simplement vide).
   • Respecte prefers-reduced-motion : affiche un terrain de football fixe.
   • Ne s'anime que lorsque la section est visible (IntersectionObserver).
   ========================================================================== */
(function () {
  const mount = document.querySelector(".lp-terrain");
  if (!mount) return;

  const SVGNS = "http://www.w3.org/2000/svg";
  const reduce =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Repère : viewBox et aire de jeu. Les MARGES (autour de `f`) accueillent les
  // cages, comme dans l'app où elles sont dessinées hors de l'aire colorée.
  const VB = { w: 240, h: 150 };
  const f = { x: 18, y: 18, w: 204, h: 114 };
  const cx = f.x + f.w / 2;
  const cy = f.y + f.h / 2;
  const r2 = (v) => Math.round(v * 100) / 100;

  /* Arc SVG conforme à ctx.arc(cx,cy,r,a0,a1) (sens horaire, y vers le bas). */
  function arcPath(ax, ay, r, a0, a1) {
    const pt = (a) => [ax + r * Math.cos(a), ay + r * Math.sin(a)];
    const [x0, y0] = pt(a0);
    const [x1, y1] = pt(a1);
    const d = (((a1 - a0) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const large = d > Math.PI ? 1 : 0;
    return `M ${r2(x0)} ${r2(y0)} A ${r2(r)} ${r2(r)} 0 ${large} 1 ${r2(x1)} ${r2(y1)}`;
  }

  // Fabriques de primitives (kind: 'draw' = tracé progressif ; 'dash' = ligne
  // pointillée en fondu ; 'fill' = pastille pleine en fondu).
  const rect = (x, y, w, h, o = {}) => ({ el: "rect", a: { x, y, width: w, height: h }, ...o });
  const line = (x1, y1, x2, y2, o = {}) => ({ el: "line", a: { x1, y1, x2, y2 }, ...o });
  const circle = (ccx, ccy, r, o = {}) => ({ el: "circle", a: { cx: ccx, cy: ccy, r }, ...o });
  const path = (d, o = {}) => ({ el: "path", a: { d }, ...o });

  const GOAL = "#334155"; // cage (marge) — sombre, comme goal() dans render.js

  /* Cage vue de dessus : cadre + poteaux, posée sur la marge. */
  function goalPrims(side) {
    const gd = f.w * 0.03, gch = f.h * 0.2;
    const lineX = side === "left" ? f.x : f.x + f.w;
    const bx = side === "left" ? lineX - gd : lineX;
    return [
      rect(bx, cy - gch / 2, gd, gch, { stroke: GOAL, sw: 1 }),
      line(lineX, cy - gch / 2, lineX, cy + gch / 2, { stroke: GOAL, sw: 2 }),
    ];
  }

  // --- Terrains (fractions identiques à render.js) --------------------------
  const SPORTS = [
    {
      key: "football",
      color: "#2F8A4E",
      build() {
        const bh = f.h * 0.55, bw = f.w * 0.13, gh = f.h * 0.26, gw = f.w * 0.05;
        return [
          rect(f.x, f.y, f.w, f.h),
          line(cx, f.y, cx, f.y + f.h),
          circle(cx, cy, f.h * 0.13),
          rect(f.x, cy - bh / 2, bw, bh),
          rect(f.x + f.w - bw, cy - bh / 2, bw, bh),
          rect(f.x, cy - gh / 2, gw, gh),
          rect(f.x + f.w - gw, cy - gh / 2, gw, gh),
          circle(cx, cy, 1.7, { kind: "fill" }),
          ...goalPrims("left"), ...goalPrims("right"),
        ];
      },
    },
    {
      key: "basketball",
      color: "#C6904B",
      build() {
        const keyW = f.w * 0.18, keyH = f.h * 0.34, ftR = f.h * 0.12, tpR = f.h * 0.46;
        const k = Math.PI / 2 - Math.PI / 2.3;
        const end = (side) => {
          const baseX = side === "left" ? f.x : f.x + f.w;
          const dir = side === "left" ? 1 : -1;
          const ftx = baseX + dir * keyW;
          return [
            rect(side === "left" ? baseX : baseX - keyW, cy - keyH / 2, keyW, keyH),
            path(arcPath(ftx, cy, ftR, side === "left" ? -Math.PI / 2 : Math.PI / 2,
              side === "left" ? Math.PI / 2 : Math.PI * 1.5)),
            path(arcPath(baseX, cy, tpR,
              side === "left" ? -Math.PI / 2.3 : Math.PI / 2 + k,
              side === "left" ? Math.PI / 2.3 : Math.PI * 1.5 - k)),
            circle(baseX + dir * (f.w * 0.015), cy, f.w * 0.008),
          ];
        };
        return [
          rect(f.x, f.y, f.w, f.h),
          line(cx, f.y, cx, f.y + f.h),
          circle(cx, cy, f.h * 0.12),
          ...end("left"), ...end("right"),
        ];
      },
    },
    {
      key: "handball",
      color: "#2A6FA0",
      build() {
        const r6 = f.h * 0.34, r9 = f.h * 0.47;
        return [
          rect(f.x, f.y, f.w, f.h),
          line(cx, f.y, cx, f.y + f.h),
          path(arcPath(f.x, cy, r6, -Math.PI / 2, Math.PI / 2)),
          path(arcPath(f.x + f.w, cy, r6, Math.PI / 2, Math.PI * 1.5)),
          path(arcPath(f.x, cy, r9, -Math.PI / 2, Math.PI / 2), { kind: "dash" }),
          path(arcPath(f.x + f.w, cy, r9, Math.PI / 2, Math.PI * 1.5), { kind: "dash" }),
          ...goalPrims("left"), ...goalPrims("right"),
        ];
      },
    },
    {
      key: "volleyball",
      color: "#C77A45",
      build() {
        const m = f.w * 0.1, cw = f.w - 2 * m, cl = f.x + m;
        const att = (cw / 2) / 3, over = f.h * 0.06;
        return [
          rect(cl, f.y, cw, f.h),
          line(cx, f.y - over, cx, f.y + f.h + over, { sw: 2.4 }),
          line(cx - att, f.y, cx - att, f.y + f.h, { kind: "dash" }),
          line(cx + att, f.y, cx + att, f.y + f.h, { kind: "dash" }),
        ];
      },
    },
    {
      key: "badminton",
      color: "#3B7A9E",
      build() {
        const back = f.w * 0.06, sv = f.w * 0.16, tram = f.h * 0.07, mid = cy;
        return [
          rect(f.x, f.y, f.w, f.h),
          line(cx, f.y, cx, f.y + f.h, { sw: 2.2 }),                 // filet
          line(f.x + back, f.y, f.x + back, f.y + f.h),              // fond de service double
          line(f.x + f.w - back, f.y, f.x + f.w - back, f.y + f.h),
          line(cx - sv, f.y, cx - sv, f.y + f.h),                    // lignes de service courtes
          line(cx + sv, f.y, cx + sv, f.y + f.h),
          line(f.x, f.y + tram, f.x + f.w, f.y + tram),              // couloirs (simple)
          line(f.x, f.y + f.h - tram, f.x + f.w, f.y + f.h - tram),
          line(f.x + back, mid, cx - sv, mid),                       // ligne centrale de service
          line(cx + sv, mid, f.x + f.w - back, mid),
        ];
      },
    },
    {
      key: "tennis",
      color: "#3C7A52",
      build() {
        const m = f.w * 0.1, xL = f.x + m, xR = f.x + f.w - m;
        const tram = f.h * 0.11, sv = (cx - xL) * 0.55, mid = cy;
        return [
          rect(xL, f.y, f.w - 2 * m, f.h),                          // court (lignes de fond = bords)
          line(cx, f.y, cx, f.y + f.h, { sw: 2 }),                  // filet
          line(xL, f.y + tram, xR, f.y + tram),                     // couloirs simples
          line(xL, f.y + f.h - tram, xR, f.y + f.h - tram),
          line(cx - sv, f.y + tram, cx - sv, f.y + f.h - tram),     // lignes de service
          line(cx + sv, f.y + tram, cx + sv, f.y + f.h - tram),
          line(cx - sv, mid, cx + sv, mid),                         // ligne médiane de service
        ];
      },
    },
  ];

  // --- Construction du SVG ---------------------------------------------------
  const svg = document.createElementNS(SVGNS, "svg");
  svg.setAttribute("viewBox", `0 0 ${VB.w} ${VB.h}`);
  svg.setAttribute("role", "img");
  const bg = document.createElementNS(SVGNS, "rect");
  bg.setAttribute("class", "lp-terrain__field");
  bg.setAttribute("x", f.x); bg.setAttribute("y", f.y);
  bg.setAttribute("width", f.w); bg.setAttribute("height", f.h);
  bg.setAttribute("rx", 3);
  const g = document.createElementNS(SVGNS, "g");
  svg.appendChild(bg); svg.appendChild(g);
  mount.appendChild(svg);

  function createEl(p) {
    const n = document.createElementNS(SVGNS, p.el);
    for (const key in p.a) {
      const v = p.a[key];
      n.setAttribute(key, typeof v === "number" ? r2(v) : v);
    }
    const kind = p.kind || "draw";
    if (kind === "fill") {
      n.setAttribute("fill", p.fill || "#fff");
      n.setAttribute("class", "mk-fill");
    } else {
      n.setAttribute("fill", "none");
      n.setAttribute("stroke", p.stroke || "#fff");
      n.setAttribute("stroke-width", p.sw || 1.5);
      n.setAttribute("stroke-linecap", "round");
      n.setAttribute("stroke-linejoin", "round");
      if (kind === "dash") {
        n.setAttribute("stroke-dasharray", "3 2.4");
        n.setAttribute("class", "mk-dash");
      } else {
        n.setAttribute("pathLength", "1"); // normalise le tracé progressif
        n.setAttribute("class", "mk");
      }
    }
    return n;
  }

  // --- Boucle d'animation ----------------------------------------------------
  let nodes = [], idx = 0, gen = 0, running = false, timer = null;

  function build(sport) {
    bg.setAttribute("fill", sport.color);
    while (g.firstChild) g.removeChild(g.firstChild);
    nodes = sport.build().map(createEl);
    nodes.forEach((n) => g.appendChild(n));
  }
  function drawOn() {
    nodes.forEach((n, i) => (n.style.transitionDelay = i * 50 + "ms"));
    void svg.getBoundingClientRect(); // applique l'état initial avant transition
    window.requestAnimationFrame(() =>
      nodes.forEach((n) => n.classList.add("is-on"))
    );
  }
  function drawOff() {
    const n = nodes.length;
    nodes.forEach((el, i) => {
      el.style.transitionDelay = (n - 1 - i) * 28 + "ms";
      el.classList.remove("is-on");
    });
  }

  function loop(myGen) {
    if (myGen !== gen) return;
    build(SPORTS[idx]);
    drawOn();
    const dn = nodes.length;
    timer = window.setTimeout(() => {
      if (myGen !== gen) return;
      drawOff();
      timer = window.setTimeout(() => {
        if (myGen !== gen) return;
        idx = (idx + 1) % SPORTS.length;
        loop(myGen);
      }, dn * 28 + 550);
    }, dn * 50 + 550 + 1000);
  }

  function start() {
    if (running) return;
    running = true;
    gen += 1;
    loop(gen);
  }
  function stop() {
    running = false;
    gen += 1;
    if (timer) window.clearTimeout(timer);
  }

  function init() {
    // Mouvement réduit : terrain de football fixe, entièrement tracé.
    if (reduce) {
      mount.classList.add("is-static");
      build(SPORTS[0]);
      nodes.forEach((n) => n.classList.add("is-on"));
      return;
    }
    if (!("IntersectionObserver" in window)) {
      start();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0.2 }
    );
    io.observe(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
